import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import { mapCustomFieldsToForm } from "go-app/components/CustomFields";
import CustomFieldsForm from "go-app/components/CustomFields/CustomFieldsForm";
import handleError from "go-app/services/errors";
import { formatDateToString } from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import AttachmentsForm from "../../../../../../../../../../../../components/Attachment/AttachmentsForm";
import { TagsFormInput } from "../../../../../../../../../../../../components/Tag/TagsFormInput";
import { apiOrganization } from "../../../../../../../../../../../../services/Api/Organization/apiOrganization";
import { PurchaseCorrectionApi, TaxApi } from "../../../../../../../../../../../../services/Api/Organization/types";
import { PurchaseContext } from "../../../../pages/Preview";
import PurchaseCorrectionFormItemsTable from "./ItemsTable/PurchaseCorrectionFormItemsTable";
import PurchaseCorrectionBasicInfoForm from "./PurchaseCorrectionBasicInfoForm";
import PurchaseCorrectionCommentForm from "./PurchaseCorrectionCommentForm";
import { getParsedPurchaseCorrectionItemsToForm, purchaseCorrectionInclude } from "./utils";

interface Props {
	correction?: PurchaseCorrectionApi;
	setCorrection?: (correction: PurchaseCorrectionApi) => void;
	purchaseCorrectionCustomFieldsConfig: CustomFieldTemplateApi[];
}

const PurchaseCorrectionForm = ({ purchaseCorrectionCustomFieldsConfig, correction, setCorrection }: Props) => {
	const { purchase, purchaseCorrections, fetchPurchaseCorrections } = useContext(PurchaseContext);
	const form = useForm<PurchaseCorrectionApi>({
		defaultValues: {
			...correction,
			corrected_at: correction?.corrected_at ?? formatDateToString(new Date()),
			custom_fields: mapCustomFieldsToForm(
				purchaseCorrectionCustomFieldsConfig || [],
				correction?.custom_fields || []
			),
			items: getParsedPurchaseCorrectionItemsToForm(purchaseCorrections || [], purchase, correction),
		},
		criteriaMode: "all",
	});
	const { setError, formState, handleSubmit, reset, clearErrors } = form;
	const { t } = useTranslation();
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const [loading, setLoading] = useState(false);
	const [taxes, setTaxes] = useState<TaxApi[] | undefined>();
	const { addFlash, addSuccessFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const history = useHistory();

	useEffect(() => {
		fetchTaxes();
	}, []);

	const fetchTaxes = async () => {
		try {
			const res = await apiOrganization.getTaxes({ size: 0 });
			setTaxes(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const getParsedFormData = (data: PurchaseCorrectionApi) => {
		data.items = [
			...data.items.filter(
				(item) =>
					item.volume ||
					item.product?.name ||
					item.quantity ||
					item.tax_id ||
					item.price_net_money?.amount ||
					item.price_gross_money?.amount
			),
		];

		data.items = data.items.map((item) => {
			if (purchase.tax_gross) {
				item.price_net_money = item.price_gross_money;
				item.price_gross_money = null;
			}
			if (purchase.tax_gross) {
				item.total_price_net_money = item.total_price_gross_money;
				item.total_price_gross_money = null;
			}

			return item;
		});

		return data;
	};

	const onSubmit = handleSubmit(async (data) => {
		if (!validateCustomErrors()) return;

		setLoading(true);
		try {
			const parsedData = getParsedFormData(data);
			let res: PurchaseCorrectionApi;
			if (correction) {
				res = await apiOrganization.updatePurchaseCorrection(purchase.id, correction.id, parsedData, {
					include: purchaseCorrectionInclude,
				});
				if (setCorrection) setCorrection(res);
				reset({
					...res,
					corrected_at: res?.corrected_at ?? formatDateToString(new Date()),
					custom_fields: mapCustomFieldsToForm(
						purchaseCorrectionCustomFieldsConfig || [],
						res?.custom_fields || []
					),
					items: getParsedPurchaseCorrectionItemsToForm(purchaseCorrections || [], purchase, res),
				});
				await fetchPurchaseCorrections();
				addSuccessFlash(t("lib:common.flash.saved"));
			} else {
				res = await apiOrganization.createPurchaseCorrection(purchase.id, parsedData, {
					include: purchaseCorrectionInclude,
				});
				await fetchPurchaseCorrections();
				reset({
					...res,
					corrected_at: res?.corrected_at ?? formatDateToString(new Date()),
					custom_fields: mapCustomFieldsToForm(
						purchaseCorrectionCustomFieldsConfig || [],
						res?.custom_fields || []
					),
					items: getParsedPurchaseCorrectionItemsToForm(purchaseCorrections || [], purchase, res),
				});
				addSuccessFlash(t("lib:common.flash.saved"));
				history.push(`/${organization.id}/documents/purchases/${purchase.id}/corrections/${res.id}`);
			}
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	if (!taxes) return <LoadingContainer />;

	return (
		<FormDirty
			formState={formState}
			loading={loading}
			onSubmit={(data) => {
				clearErrors();
				onSubmit(data);
			}}
		>
			<PurchaseCorrectionBasicInfoForm form={form} setErrors={setErrors} />
			{purchaseCorrectionCustomFieldsConfig.length > 0 && (
				<fieldset className="form-group">
					<h5>{t("common.word.custom_fields")}</h5>
					<CustomFieldsForm
						customFieldsConfig={purchaseCorrectionCustomFieldsConfig}
						customFields={correction?.custom_fields || []}
						form={form}
						setErrors={setErrors}
					/>
				</fieldset>
			)}
			<PurchaseCorrectionFormItemsTable form={form} taxes={taxes} />
			<PurchaseCorrectionCommentForm form={form} />
			<h5>{t("lib:common.word.advanced")}</h5>
			<div>
				<TagsFormInput form={form} />
				<span className="mt-4 d-block">{t("common.word.attachments")}</span>
				<AttachmentsForm form={form} />
			</div>
		</FormDirty>
	);
};

export default PurchaseCorrectionForm;
